import { Cctv } from "lucide-react";

const PUFFS = [
  { cls: "smoke-puff-r", size: 26, left: "0px", delay: "0s" },
  { cls: "smoke-puff-l", size: 34, left: "14px", delay: "1.4s" },
  { cls: "smoke-puff-r", size: 22, left: "-12px", delay: "2.6s" },
  { cls: "smoke-puff-l", size: 30, left: "6px", delay: "3.8s" },
  { cls: "smoke-puff-r", size: 18, left: "22px", delay: "4.7s" },
];

const Bracket = ({ pos }) => (
  <span className={`absolute h-3.5 w-3.5 border-[#62C3F0] ${pos}`} aria-hidden="true" />
);

export const SmokeScene = () => (
  <div>
    <div
      className="relative aspect-[16/11] w-full overflow-hidden rounded-2xl border border-white/10 bg-[#090D10]"
      data-testid="smoke-scene"
      role="img"
      aria-label="Animatie: rook stijgt op uit een afvalhoop en wordt door de camera gedetecteerd, terwijl de temperatuur nog normaal is"
    >
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(180deg, #0c151c 0%, #090D10 55%)" }}
        aria-hidden="true"
      />

      {/* detection cone from camera */}
      <div
        className="cone-sweep absolute right-[9%] top-[13%] h-[62%] w-[68%]"
        style={{
          clipPath: "polygon(100% 0%, 0% 52%, 42% 100%)",
          background: "linear-gradient(225deg, rgba(98,195,240,0.22), rgba(98,195,240,0.04) 55%, transparent 75%)",
        }}
        aria-hidden="true"
      />

      {/* camera */}
      <div className="absolute right-[5%] top-[7%] flex flex-col items-center gap-1.5" aria-hidden="true">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#2B9FD7]/50 bg-[#10232F] text-[#62C3F0] shadow-[0_0_18px_rgba(43,159,215,0.35)]">
          <Cctv size={22} strokeWidth={1.6} style={{ transform: "scaleX(-1)" }} />
        </span>
        <span className="font-heading text-[9px] font-semibold uppercase tracking-[0.2em] text-[#CBD3D9]/60">
          Camera
        </span>
      </div>

      {/* waste pile */}
      <svg viewBox="0 0 400 260" preserveAspectRatio="none" className="absolute inset-x-0 bottom-0 h-[46%] w-full" aria-hidden="true">
        <path d="M0,260 L0,120 C40,96 80,110 120,88 C160,66 200,84 240,74 C290,62 330,86 400,72 L400,260 Z" fill="#141d25" />
        <path d="M0,260 L0,160 C50,140 100,150 150,128 C210,102 260,124 320,112 C360,105 385,118 400,112 L400,260 Z" fill="#1b2833" />
        <g stroke="#2B9FD7" strokeOpacity="0.18" strokeWidth="1.5">
          <path d="M60,150 l16,-8 M110,132 l14,10 M170,112 l18,-6 M235,118 l14,8 M300,108 l16,-7 M340,122 l12,9 M90,180 l18,-5 M200,150 l16,7 M270,146 l15,-8" />
        </g>
        <g fill="#CBD3D9" fillOpacity="0.1">
          <circle cx="85" cy="162" r="4" /><circle cx="150" cy="140" r="5" /><circle cx="225" cy="132" r="4" /><circle cx="310" cy="126" r="5" /><circle cx="360" cy="140" r="3.5" />
        </g>
      </svg>

      {/* rising smoke */}
      <div className="absolute left-[36%] top-[48%]" aria-hidden="true">
        {PUFFS.map((puff, i) => (
          <span
            key={i}
            className={`${puff.cls} absolute rounded-full bg-[#CBD3D9] blur-md`}
            style={{ width: puff.size, height: puff.size, left: puff.left, animationDelay: puff.delay, opacity: 0 }}
          />
        ))}
      </div>

      {/* dashed detection link camera -> smoke */}
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="detect-anim absolute inset-0 h-full w-full" aria-hidden="true">
        <line x1="86" y1="15" x2="49" y2="33" stroke="#62C3F0" strokeOpacity="0.7" strokeWidth="1" vectorEffect="non-scaling-stroke" className="tech-line" />
      </svg>

      {/* detection reticle around smoke */}
      <div className="detect-anim absolute left-[27%] top-[20%] h-[40%] w-[24%]" aria-hidden="true">
        <Bracket pos="left-0 top-0 border-l-2 border-t-2" />
        <Bracket pos="right-0 top-0 border-r-2 border-t-2" />
        <Bracket pos="bottom-0 left-0 border-b-2 border-l-2" />
        <Bracket pos="bottom-0 right-0 border-b-2 border-r-2" />
        <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-[#62C3F0] px-1.5 py-0.5 font-heading text-[8px] font-bold uppercase tracking-[0.15em] text-[#090D10]">
          Rook
        </span>
      </div>

      {/* alert badge */}
      <div className="badge-anim absolute left-3 top-3 rounded-lg border border-[#FF8A00]/50 bg-[#090D10]/90 px-3.5 py-2.5 backdrop-blur-md" data-testid="smoke-scene-alert">
        <p className="flex items-center gap-2 font-heading text-[11px] font-bold uppercase tracking-[0.15em] text-[#FF8A00]">
          <span className="h-2 w-2 animate-pulse rounded-full bg-[#FF8A00]" /> Rook gedetecteerd
        </p>
        <p className="mt-1 text-[10px] text-[#CBD3D9]/80">Nog geen hitte- of vuurontwikkeling</p>
      </div>

      {/* temperature stays normal */}
      <div className="absolute bottom-3 left-3 flex items-center gap-2 rounded-full border border-[#2B9FD7]/40 bg-[#090D10]/85 px-3.5 py-1.5 backdrop-blur-md" data-testid="smoke-scene-temp">
        <span className="h-2 w-2 rounded-full bg-[#2B9FD7]" />
        <span className="font-heading text-[11px] font-bold tabular-nums text-[#62C3F0]">21,4 °C</span>
        <span className="text-[10px] uppercase tracking-[0.15em] text-[#CBD3D9]/70">Normaal</span>
      </div>

      <span className="absolute bottom-3 right-3 rounded bg-[#090D10]/70 px-2 py-1 text-[9px] text-[#CBD3D9]/60 backdrop-blur-sm">
        Visualisatie ter illustratie.
      </span>
    </div>

    <ol className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-[#CBD3D9]/70">
      <li className="flex items-center gap-1.5">
        <span className="font-heading font-bold text-[#62C3F0]">1</span> Rook ontstaat
      </li>
      <li aria-hidden="true" className="text-[#CBD3D9]/30">→</li>
      <li className="flex items-center gap-1.5">
        <span className="font-heading font-bold text-[#62C3F0]">2</span> Camera detecteert
      </li>
      <li aria-hidden="true" className="text-[#CBD3D9]/30">→</li>
      <li className="flex items-center gap-1.5">
        <span className="font-heading font-bold text-[#FF8A00]">3</span> Melding — vóórdat hitte of vuur ontstaat
      </li>
    </ol>
  </div>
);
